import React, { useEffect, useRef, useState, Dispatch, SetStateAction, useCallback } from 'react';
import { ChatLogo } from '../chatlogo';
import { ChatMessageResponse } from '../chatmessageresponse';
import { ChatSkeletonLoader } from '../chat-skeleton-loader';
import CtyPromptInput, { promptInputType, UploadedFileType, CtyShowToast, FileUpload, CtyButton, formatTimestamp, ErrorBoundary, CommandFormGroup, CommandFormReadOnly, CommandFormPromptInput, applicationType, ApplicationType, isCompactApplicationType } from '@circuitry-ai/doc-ui';
import { useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation, type WebAgentStep } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import KnowledgeSourceUI from '../knowledgeSourceUI';
import { AgentChatOptions } from './agent-chat-options';
import { AdvisorThinkingProcess } from '../AdvisorThinkingProcess';
import { JsonTreeView } from './JsonTreeView';

// Hoist ErrorBoundary-wrapped components to module scope
const ChatLogoWithErrorBoundary = ErrorBoundary(ChatLogo);
const JsonTreeViewWithErrorBoundary = ErrorBoundary(JsonTreeView as unknown as React.ComponentType<Record<string, unknown>>);
const ChatMessageResponseWithErrorBoundary = ErrorBoundary(ChatMessageResponse);
const ChatSkeletonLoaderWithErrorBoundary = ErrorBoundary(ChatSkeletonLoader);
const CtyPromptInputWithErrorBoundary = ErrorBoundary(CtyPromptInput);
const KnowledgeSourceUIWithErrorBoundary = ErrorBoundary(KnowledgeSourceUI);
const AgentChatOptionsWithErrorBoundary = ErrorBoundary(AgentChatOptions);
const FileUploadWithErrorBoundary = ErrorBoundary(FileUpload as unknown as React.ComponentType<Record<string, unknown>>);

export interface SharedConverseUiProps {
    name?: string;
    responseLoading?: boolean;
    handleSend: (userInput: string, uploadedFiles: UploadedFileType[]) => void;
    handleRerun?: (userInput: string, messageIndex: number) => void;
    converseMessages?: any[];
    setSourceId?: Dispatch<SetStateAction<Record<string, string>>>;
    showAccordion?: boolean;
    aiworker_id?: string;
    session_id?: string;
    customInput?: string;
    autoExecute?: boolean;
    hideTopHeader?: boolean;
    commandJson?: Record<string, any>;
    commandFormData?: Record<string, string>;
    commandCustomMessage?: string;
    onCommandFormDataChange?: (data: Record<string, string>) => void;
    onCommandCustomMessageChange?: (msg: string) => void;
    commandSteps?: ReadonlyArray<WebAgentStep>;
    enableFile?: boolean;
    enableSpeak?: boolean;
    promptPrefix?: React.ReactNode;
    placeholder?: string;
    showStepDuration?: boolean;
}

// --- HOISTED CONTENT COMPONENT ---
interface SharedConverseContentProps {
    name: string;
    responseLoading: boolean;
    converseMessages: any[];
    handleUserInputSend: (userInput: string) => void;
    handleRerun?: (userInput: string, messageIndex: number) => void;
    aiworker_id: string;
    session_id: string;
    uploadedFiles: UploadedFileType[];
    setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFileType[]>>;
    isSpeechMode: boolean;
    setIsSpeechMode: React.Dispatch<React.SetStateAction<boolean>>;
    customInput?: string;
    commandJson?: Record<string, any>;
    commandFormData?: Record<string, string>;
    commandCustomMessage?: string;
    onCommandFormDataChange?: (data: Record<string, string>) => void;
    onCommandCustomMessageChange?: (msg: string) => void;
    commandSteps?: ReadonlyArray<WebAgentStep>;
    enableFile?: boolean;
    enableSpeak?: boolean;
    promptPrefix?: React.ReactNode;
    placeholder?: string;
    showStepDuration?: boolean;
    /** Extension/widget, before the first message: size to the command form, capped at 85vh. */
    fitContent?: boolean;
}

interface ConverseMessageItemProps {
    readonly msg: any;
    readonly index: number;
    readonly aiworker_id: string;
    readonly session_id: string;
    readonly handleRerun?: (userInput: string, messageIndex: number) => void;
    readonly previousUserInput?: string;
    readonly commandJson?: Record<string, any>;
    readonly showStepDuration?: boolean;
}

const ConverseMessageItem = React.memo(function ConverseMessageItem(props: ConverseMessageItemProps) {
    const { msg, index, aiworker_id, session_id, handleRerun, previousUserInput, commandJson, showStepDuration = false } = props;
    const hasTextMessage = !!msg?.textMessage && msg.textMessage.trim().length > 0;
    const phases = Array.isArray(msg?.phases) ? msg.phases : [];
    const msgFormData = msg?.commandFormData as Record<string, string> | undefined;
    const msgCustomMessage = (msg?.commandCustomMessage ?? '') as string;

    const renderWorkflowAccordion = () => {
        if (!hasTextMessage && phases.length === 0) return null;
        return (
            <AdvisorThinkingProcess
                processes={phases}
                isComplete={hasTextMessage}
                isStreaming={!hasTextMessage}
                variant="agent"
            />
        );
    };

    return (
        <div className={`flex flex-row items-start w-full gap-3 bg-yellow-s300`}>
            <ChatLogoWithErrorBoundary viewLogo={msg.viewLogo} advisorLogo={msg?.advisorLogo} initials={msg?.initials} />
            <div className="flex-1 overflow-x-auto relative flex flex-col gap-2 p-2" style={{ width: '100px' }}>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${msg.viewLogo ? 'text-base' : 'text-[15px]'} font-bold text-foreground`}>{msg.name}</span>
                    {msg?.message_id && (
                        <CtyButton
                            tooltipContent={`${msg.message_id ?? ''}`}
                            toolTipColor='warning'
                            className="h-[32px]"
                            isIconOnly
                            radius="full"
                            size="sm"
                            variant="light"
                        >
                            <div className="relative">
                                <Icon icon="ph:info-light" width="1.2rem" height="1.1rem" style={{ color: "#afa4a4" }} />
                            </div>
                        </CtyButton>
                    )}
                    {msg?.total_time_taken_seconds && (
                        <CtyButton
                            tooltipContent={`Total time: ${formatTimestamp(msg?.total_time_taken_seconds)}`}
                            toolTipColor="warning"
                            className="h-[32px]"
                            isIconOnly
                            radius="full"
                            size="sm"
                            variant="light"
                        >
                            <div className="relative">
                                <Icon icon="ph:info-light" width="1.2rem" height="1.1rem" style={{ color: "#afa4a4" }} />
                            </div>
                        </CtyButton>
                    )}
                </div>

                {renderWorkflowAccordion()}

                {msg?.file?.length > 0 &&
                    <div className="flex flex-wrap gap-2">
                        {msg?.file?.map((file: UploadedFileType, idx: number) => (
                            <FileUploadWithErrorBoundary key={file.name + idx} file={file} />
                        ))}
                    </div>
                }
                {msgFormData && commandJson && (
                    <div className="w-[90%]">
                        <CommandFormReadOnly
                            commandJson={commandJson}
                            formData={msgFormData}
                            customMessage={msgCustomMessage}
                            aiworkerId={aiworker_id}
                            formId={`message-${index}`}
                        />
                    </div>
                )}
                {msg?.structuredOutput ? (
                    <div className="space-y-2">
                        {Object.entries(msg.structuredOutput as Record<string, unknown>).map(([key, value]) => {
                            const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                            const isJsonValue = value !== null && typeof value === 'object';
                            return (
                                <div key={key} className="rounded-md border border-default-200 bg-default-100 p-3">
                                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-default-500">
                                        {label}
                                    </span>
                                    {isJsonValue ? (
                                        <JsonTreeViewWithErrorBoundary data={value} />
                                    ) : (
                                        <ChatMessageResponseWithErrorBoundary
                                            key={index + key}
                                            advisorId=""
                                            messageId=""
                                            msg={{}}
                                            translatedText={value == null ? '' : String(value as string | number | boolean)}
                                            isPartsCatelogEnabled={false}
                                            setInlineTriggerSource={() => undefined}
                                            setInlineTriggerLoading={() => undefined}
                                            inlineTriggerLoading=""
                                            setTriggerInlineFileView={() => undefined}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <ChatMessageResponseWithErrorBoundary
                        key={index + 'message'}
                        advisorId=""
                        messageId=""
                        msg={{}}
                        translatedText={msg.textMessage}
                        isPartsCatelogEnabled={false}
                        setInlineTriggerSource={() => undefined}
                        setInlineTriggerLoading={() => undefined}
                        inlineTriggerLoading=""
                        setTriggerInlineFileView={() => undefined}
                    />
                )}
                {msg.sources && msg.sources.source_ids && msg.sources.source_ids.length > 0 && (
                    <KnowledgeSourceUIWithErrorBoundary
                        metadataMap={{
                            source_ids: msg.sources.source_ids,
                            message_id: msg.message_id,
                            total_time_taken_seconds: msg.total_time_taken_seconds
                        }}
                        advisorId=""
                        inlineTriggerSource={() => undefined}
                        inlineTriggerLoading={""}
                        setInlineTriggerLoading={() => undefined}
                        agentSource={true}
                    />
                )}
                {msg?.message_id && aiworker_id && session_id && (
                    <AgentChatOptionsWithErrorBoundary
                        message={{
                            messageId: msg.message_id,
                            message: msg.textMessage,
                            metaData: msg.metaData || []
                        }}
                        aiworker_id={aiworker_id}
                        session_id={session_id}
                        msgIndex={index}
                        onRerun={handleRerun}
                        previousUserInput={previousUserInput}
                    />
                )}
            </div>
        </div>
    );
});

const SharedConverseContent = React.memo((props: SharedConverseContentProps) => {
    const {
        name,
        responseLoading,
        converseMessages,
        handleUserInputSend,
        handleRerun,
        aiworker_id,
        session_id,
        uploadedFiles,
        setUploadedFiles,
        isSpeechMode,
        setIsSpeechMode,
        customInput,
        commandJson,
        commandFormData,
        commandCustomMessage,
        onCommandFormDataChange,
        onCommandCustomMessageChange,
        commandSteps,
        enableFile = true,
        enableSpeak = true,
        promptPrefix,
        placeholder = 'Start typing a question...',
        showStepDuration = false,
        fitContent = false,
    } = props;

    // Show the skeleton only until the first streamed phase arrives; once phases
    // exist, AdvisorThinkingProcess covers the in-progress state instead.
    const hasWorkflowProgressVisible = converseMessages.some(
        (msg) => Array.isArray(msg?.phases) && msg.phases.length > 0
    );

    const phaseCount = converseMessages.reduce(
        (sum, msg) => sum + (Array.isArray(msg?.phases) ? msg.phases.length : 0),
        0
    );

    const showResponseSpinner = responseLoading && !hasWorkflowProgressVisible;
    const hidePromptWhileRunning = responseLoading && applicationType() === ApplicationType.BROWSER_EXTENSION;

    const endOfMessagesRef = useRef<HTMLDivElement | null>(null);
    const messagesScrollRef = useRef<HTMLDivElement | null>(null);
    const fitContentBoxRef = useRef<HTMLDivElement | null>(null);
    const fitContentHeightRef = useRef(0);

    useEffect(() => {
        if (endOfMessagesRef.current) {
            endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [converseMessages.length, phaseCount]);

    useEffect(() => {
        const scrollEl = messagesScrollRef.current;
        if (!scrollEl) return undefined;

        const pinToBottom = () => {
            scrollEl.scrollTop = scrollEl.scrollHeight;
        };

        const observer = new ResizeObserver(pinToBottom);
        observer.observe(scrollEl);
        return () => observer.disconnect();
    }, [phaseCount, converseMessages.length]);

    // Remember the tallest size while the form is open. Collapsing the form keeps that
    // height, and the space the fields left becomes empty surface inside the section.
    useEffect(() => {
        const box = fitContentBoxRef.current;
        if (!box) return undefined;
        if (!fitContent) {
            fitContentHeightRef.current = 0;
            box.style.minHeight = '';
            return undefined;
        }
        const observer = new ResizeObserver(() => {
            const next = box.offsetHeight;
            if (next <= fitContentHeightRef.current + 1) return;
            fitContentHeightRef.current = next;
            box.style.minHeight = `${next}px`;
        });
        observer.observe(box);
        return () => {
            observer.disconnect();
            fitContentHeightRef.current = 0;
            box.style.minHeight = '';
        };
    }, [fitContent]);

    return (
        <CommandFormGroup>
        <div ref={fitContentBoxRef} className={`flex min-h-0 flex-col overflow-hidden border-1 rounded-medium ${fitContent ? 'max-h-full shrink bg-content1' : 'h-full flex-1'}`}>
            <div className="flex flex-col gap-1 p-3 border-b-1 ml-2 flex-shrink-0">
                <span className="text-sm font-semibold text-default-700">{name}</span>
                <span className="text-xs text-default-500"><span className="inline-flex items-center rounded-full bg-success h-2 w-2 mr-2"></span>Active now</span>
            </div>
            <div className={`flex min-h-0 flex-col overflow-hidden ${fitContent ? 'shrink' : 'flex-1'}`}>
                {fitContent && !showResponseSpinner ? null : (
                <div ref={messagesScrollRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-2">
                    {converseMessages.map((msg, index) => (
                        <ConverseMessageItem
                            key={index + 'agent-chat'}
                            msg={msg}
                            index={index}
                            aiworker_id={aiworker_id}
                            session_id={session_id}
                            handleRerun={handleRerun}
                            previousUserInput={index > 0 ? converseMessages[index - 1]?.textMessage : undefined}
                            commandJson={commandJson}
                            showStepDuration={showStepDuration}
                        />
                    ))}
                    <ChatSkeletonLoaderWithErrorBoundary isLoading={showResponseSpinner} />
                    <div ref={endOfMessagesRef} />
                </div>
                )}
                {hidePromptWhileRunning ? null : (
                    <>
                        {promptPrefix ? <div className="shrink-0">{promptPrefix}</div> : null}
                        {commandJson ? (
                            <div className="flex min-h-0 max-h-full shrink flex-col overflow-hidden">
                            <CommandFormPromptInput
                                commandJson={commandJson}
                                formData={commandFormData ?? {}}
                                customMessage={commandCustomMessage ?? ''}
                                onFormDataChange={onCommandFormDataChange ?? (() => undefined)}
                                onCustomMessageChange={onCommandCustomMessageChange ?? (() => undefined)}
                                onSend={() => handleUserInputSend('')}
                                aiworkerId={aiworker_id}
                                steps={commandSteps}
                                isDisabled={responseLoading}
                            />
                            </div>
                        ) : (
                            <div className="flex w-full flex-col items-start rounded-medium bg-content1 transition-colors hover:bg-content1  border chat-converse-input">
                                <CtyPromptInputWithErrorBoundary
                                    elementType={promptInputType.TEXTAREA}
                                    id='chatinput-textarea'
                                    classNames={{
                                        inputWrapper: `!bg-transparent shadow-none  `,
                                        innerWrapper: "relative",
                                        input: "pt-1 pb-6 pl-2 !pr-10 text-medium",
                                    }}
                                    radius="lg"
                                    variant="flat"
                                    handleSendClick={handleUserInputSend}
                                    placeholder={placeholder}
                                    enableSpeak={enableSpeak}
                                    enableSendBtn={true}
                                    setIsSpeechMode={setIsSpeechMode}
                                    isSpeechMode={isSpeechMode}
                                    uploadedFiles={uploadedFiles}
                                    enableFile={enableFile}
                                    setUploadedFiles={setUploadedFiles}
                                    defaultValue={customInput || ''}
                                    isDisabled={responseLoading}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
        </CommandFormGroup>
    );
});
// --- END HOISTED CONTENT COMPONENT ---

const SharedConverseUI = (props: SharedConverseUiProps) => {
    const {
        name = '',
        responseLoading = false,
        handleSend,
        handleRerun,
        converseMessages = [],
        setSourceId,
        showAccordion = false,
        aiworker_id = '',
        session_id = '',
        customInput,
        autoExecute = false,
        commandJson,
        commandFormData,
        commandCustomMessage,
        onCommandFormDataChange,
        onCommandCustomMessageChange,
        commandSteps,
        enableFile = true,
        enableSpeak = true,
        promptPrefix,
        placeholder,
        showStepDuration = false,
    } = props;

    const [isSpeechMode, setIsSpeechMode] = useState<boolean>(false);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);

    const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
    const [uploadtos3] = useUploadFileToS3Mutation();

    const uploadFiles = useCallback(async (files: UploadedFileType[]) => {
        if (!files.length) return;

        try {
            const length = files.length;
            const currentFile = files[length - 1];
            const payLoad = [
                {
                    filename: currentFile.name,
                    source_type: 'action'
                }
            ];

            const uploadURL: any = await getAiworkersPresignedUrl(payLoad).unwrap();

            await uploadtos3({
                file: currentFile.file,
                presignedUrl: uploadURL?.[0]?.presigned_url,
                CtyShowToast
            }).unwrap();

            currentFile.uploading = false;
            setUploadedFiles((prev) => {
                const updatedFiles = [...prev];
                updatedFiles[length - 1] = currentFile;
                return updatedFiles;
            });

            setSourceId?.((prev: Record<string, string>) => ({
                ...prev,
                [uploadURL?.[0]?.source_id as string]: currentFile.name,
            }));

        } catch (err) {
            console.error('Error uploading files:', err);
        }
    }, [getAiworkersPresignedUrl, uploadtos3, setSourceId]);

    useEffect(() => {
        const length = uploadedFiles?.length ?? 0;
        if (length === 0 || uploadedFiles[length - 1].uploading === false) return;
        uploadFiles(uploadedFiles);
    }, [uploadedFiles, uploadFiles]);

    const handleUserInputSend = useCallback((userInput: string) => {
        if (responseLoading) return;
        handleSend(userInput, uploadedFiles);
        setUploadedFiles([]);
    }, [handleSend, responseLoading, uploadedFiles]);

    const fillScreen = isCompactApplicationType() && converseMessages.length === 0;

    const contentProps: SharedConverseContentProps = {
        name,
        responseLoading,
        converseMessages,
        handleUserInputSend,
        handleRerun,
        aiworker_id,
        session_id,
        uploadedFiles,
        setUploadedFiles,
        isSpeechMode,
        setIsSpeechMode,
        customInput,
        commandJson,
        commandFormData,
        commandCustomMessage,
        onCommandFormDataChange,
        onCommandCustomMessageChange,
        commandSteps,
        enableFile,
        enableSpeak,
        promptPrefix,
        placeholder,
        showStepDuration,
        fitContent: fillScreen,
    };

    // Extension and widget, before the first message: grow with the command form, never past 85vh.
    const heightClass = fillScreen ? 'max-h-[85vh]' : 'h-full';

    return (
        <div className={`flex min-h-0 flex-col overflow-hidden ${showAccordion ? 'mt-2' : ''} ${heightClass}`}>
            <div className={`flex min-h-0 flex-col overflow-hidden ${fillScreen ? 'max-h-full shrink' : 'flex-1'}`}>
                <SharedConverseContent {...contentProps} />
            </div>
        </div>
    );
};

export { SharedConverseUI };